import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import TableGrid from './TableGrid/index';
import { useParams } from 'react-router-dom';
import { useGlobalMetadata } from '../utils/providers';
import { useRecordNavigator } from '../hooks/useRecordNavigator';
import SubmissionComposer from './SubmissionComposer/SubmissionComposer';
import { DateRangeSelect, ALL_TIME_PRESET, PAST_30_DAYS_PRESET, presetToFilterRange } from './ui/DateRangeSelect';
import { FilterBar, FilterBarControl } from './ui/FilterBar';
import type { CRMFilterCondition, GridConfiguration } from '../types/grid';
import type { ProcessedMetadata } from '../utils/metadata/main-metadata';
import type { RouteMetadata, UIComponentMetadata } from '../types/metadata';
import RepPage from './RepPage/RepPage';
import SubmissionPage from './SubmissionPage/SubmissionPage';
import FunderPage from './FunderPage/FunderPage';
import LeadSourcePage from './LeadSourcePage/LeadSourcePage';
import ImportsPage from './ImportsPage/ImportsPage';
import CreateListRecordButton from './CreateListRecordButton';
import { useMyCapabilities } from '../utils/metadata/capabilities';
import { useIsInstantlyPartner } from '../hooks/useIsInstantlyPartner';

// Interface for URL parameters
interface RecordListParams extends Record<string, string | undefined> {
  table: string;
}

// Interface for extended metadata with proper route data
interface ExtendedProcessedMetadata extends ProcessedMetadata {
  routeData: RouteMetadata;
}

function RecordList(): ReactElement {
  const { table: tableFromUrl } = useParams<RecordListParams>();
  const { data: metadata } = useGlobalMetadata();
  const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  const { openRecord } = useRecordNavigator();
  const [gridRefreshKey, setGridRefreshKey] = useState(0);
  const [datePreset, setDatePreset] = useState(PAST_30_DAYS_PRESET);
  // The Instantly partner (REG-922) reads his few deals: all dates, no composer.
  const isInstantlyPartner = useIsInstantlyPartner();
  useEffect(() => {
    if (isInstantlyPartner) setDatePreset(ALL_TIME_PRESET);
  }, [isInstantlyPartner]);

  // Type assertion for metadata with proper route structure
  const typedMetadata = metadata as ExtendedProcessedMetadata;

  // Get the list configuration for this route
  let configFromUrl: UIComponentMetadata | null = typedMetadata?.routeData?.primaryUiComponentByRouteName?.[tableFromUrl as string]?.list || null;

  if (!configFromUrl) {
    throw new Error(`No list configuration found for route: ${tableFromUrl}`);
  }

  if (configFromUrl && !configFromUrl.schema_name) {
    const tableInfo = typedMetadata?.tableData?.byName?.[configFromUrl.table_name];
    configFromUrl = {
      ...configFromUrl,
      schema_name: tableInfo?.schema_name || '',
      ui_data: {
        ...configFromUrl.ui_data,
        schema_name: tableInfo?.schema_name || ''
      }
    };
  }

  const tableName = configFromUrl.table_name;
  const tableInfo = typedMetadata?.tableData?.byName?.[tableName];
  const tableLabel = tableInfo?.ui_label || tableFromUrl?.replace(/_/g, ' ') || 'Record';

  const handleRecordOpen = useCallback((recordId: string | number) => {
    openRecord({
      id: recordId,
      table: tableFromUrl as string,
      tableName,
      label: `${tableLabel} #${recordId}`,
    });
  }, [openRecord, tableFromUrl, tableName, tableLabel]);

  // Memoize grid config so it only changes when the table changes
  const gridConfiguration: GridConfiguration = useMemo(() => ({
    ...configFromUrl!,
    name: configFromUrl!.name || `${configFromUrl!.table_name}_grid`,
    ui_data: {
      ...configFromUrl!.ui_data,
      fields: configFromUrl!.ui_data.fields || []
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tableFromUrl, metadata]);

  // Date-range preset filter (merchant list only). Applied server-side on the
  // date the merchant came in (created_at), pre-split into gte/lt entries — the shape topLevelFilters
  // expects after the grid's own inRange adjustment has already run.
  const dateRangeFilters = useMemo<Record<string, CRMFilterCondition> | undefined>(() => {
    if (tableName !== 'merchant' || datePreset === ALL_TIME_PRESET) return undefined;
    const { start, endExclusive } = presetToFilterRange(datePreset);
    const filters: Record<string, CRMFilterCondition> = {};
    if (start) filters.created_at__gte = { colId: 'created_at', filterType: 'date', type: 'greaterThanOrEqual', filter: start };
    if (endExclusive) filters.created_at__lt = { colId: 'created_at', filterType: 'date', type: 'lessThan', filter: endExclusive };
    return filters;
  }, [tableName, datePreset]);

  const requiredCapability = tableInfo?.ui_data?.required_read_capability;
  if (typeof requiredCapability === 'string') {
    if (capabilitiesLoading) return <p data-page-loading="true">Checking access...</p>;
    if (!hasCapability(requiredCapability)) {
      return <p role="status" data-testid="table-access-denied">You do not have access to these records.</p>;
    }
  }

  if (tableName === 'rep') {
    return <RepPage />;
  }

  if (tableName === 'submission') {
    return (
      <>
        {!isInstantlyPartner && <SubmissionComposer />}
        <SubmissionPage />
      </>
    );
  }

  if (tableName === 'funder') {
    return <FunderPage />;
  }

  if (tableName === 'lead_import') {
    return <ImportsPage />;
  }

  if (tableName === 'lead_source') {
    return <LeadSourcePage />;
  }

  return (
    <>
      {tableName === 'merchant' && (
        <FilterBar onReset={() => setDatePreset(PAST_30_DAYS_PRESET)}>
          <FilterBarControl label="Date">
            <DateRangeSelect value={datePreset} onChange={setDatePreset} />
          </FilterBarControl>
        </FilterBar>
      )}
      <TableGrid
      gridConfig={gridConfiguration}
      routeName={tableFromUrl as string}
      onRecordOpen={handleRecordOpen}
      hideActions
      showEditModeToggle
      syncPageToUrl
      refreshKey={gridRefreshKey}
      topLevelFilters={dateRangeFilters}
      // 46px filter bar + its 10px bottom margin rendered above the grid.
      heightOffset={tableName === 'merchant' ? 56 : undefined}
      // REG-828: each user's columns are saved on the server for every
      // metadata table (Merchants keeps its original key), and admins can save
      // the table's default layout from the grid.
      layoutKey={tableName === 'merchant' ? 'merchant_list' : `table_list_${tableName}`}
      toolbarActions={tableName === 'merchant' ? (
        <>
          <CreateListRecordButton
            tableName="merchant"
            onCreated={(created) => {
              setGridRefreshKey((current) => current + 1);
              openRecord({
                id: created.id,
                table: tableFromUrl as string,
                tableName: 'merchant',
                label: created.label,
              });
            }}
          />
        </>
      ) : undefined}
      />
    </>
  );
}

export default RecordList;
